import { ArrowLeftOutlined } from '@ant-design/icons'
import { Button, Flex, Form, FormProps, Typography } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { DesignBox } from '~/plugins/designbox'
import { Toolbox } from '~/plugins/toolbox/shared'
import { StepType } from '../../../common/types'
import { ViewerFormInput } from '../ViewerFormInput'
import { ViewerFormDynamic } from '../ViewerFormInputDynamic'

type Props = {
  isReadonly: boolean
  isLastStep: boolean
  values: any
  valuesConfiguration?: any
  step: StepType
  onBack?: () => void
  onChange: (values: any) => void
  onChangeInstant: (values: any) => void
  onSubmit: (values: any) => void
}

export const ViewerStep: React.FC<Props> = ({
  isReadonly = false,
  isLastStep,
  values = {},
  valuesConfiguration = {},
  step,
  onBack,
  onChange,
  onChangeInstant,
  onSubmit,
}) => {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)

  const title = step.title
  const description = step.description

  const [form] = Form.useForm()

  const initialValues = useMemo(
    () => ({
      [step.key]: {
        ...(step.inputs ?? [])
          .filter(input => input.key)
          .reduce(
            (acc, input) => ({
              ...acc,
              [input.key]:
                step.initialValues?.[input.key] ??
                input.propsFormInput?.defaultValue,
            }),
            {},
          ),
        ...(values[step.key] || {}),
      },
    }),
    [step, values],
  )

  useEffect(() => {
    form.setFieldsValue(initialValues)
  }, [form, initialValues])

  const formInputs = useMemo(() => {
    const inputs = (step.inputs ?? []).filter(input => !!input.key)

    return inputs
  }, [step])

  const handleChange: FormProps['onValuesChange'] = (_, valuesUpdated) => {
    onChange({ ...values, [step.key]: valuesUpdated[step.key] })
  }

  const handleSubmit: FormProps['onFinish'] = _ => {
    const currentValues = form.getFieldsValue(true)

    const valuesEnsured = Toolbox.Utility.trimNullValues({
      [step.key]: {
        ...initialValues[step.key],
        ...currentValues[step.key],
      },
    })

    onSubmit({ ...values, [step.key]: valuesEnsured[step.key] })
  }

  const handleUploadingChange = (isUploading: boolean) => {
    setIsSubmitDisabled(isUploading)
  }

  return (
    <>
      <Flex className="w-full" vertical>
        <Flex vertical className="mb-10">
          {title && <Typography.Title level={4}>{title}</Typography.Title>}
          {description && (
            <Typography.Text type="secondary">{description}</Typography.Text>
          )}
          {/* {isReadonly && (
            <div>
              <Tag color="blue">Read Only</Tag>
            </div>
          )} */}
        </Flex>

        <Form
          form={form}
          initialValues={initialValues}
          layout="vertical"
          requiredMark={false}
          onValuesChange={handleChange}
          onFinish={handleSubmit}
        >
          {formInputs.map(formInput => {
            if (formInput.inputType === 'Dynamic') {
              return (
                <ViewerFormDynamic
                  key={`${step.key}.${formInput.key}`}
                  step={step}
                  isReadonly={isReadonly}
                  formInput={formInput as any}
                  formValuesConfiguration={valuesConfiguration}
                  formValues={values}
                  onUploadingChange={handleUploadingChange}
                >
                  {(dynamicInputs, keyDynamic) =>
                    dynamicInputs.map(dynamicInput => (
                      <ViewerFormInput
                        key={`${step.key}.${dynamicInput.key}.${keyDynamic}`}
                        keyDynamic={keyDynamic}
                        step={step}
                        isReadonly={isReadonly}
                        formInput={dynamicInput}
                        formValuesConfiguration={valuesConfiguration}
                        formValues={values}
                        onUploadingChange={handleUploadingChange}
                      />
                    ))
                  }
                </ViewerFormDynamic>
              )
            } else {
              return (
                <ViewerFormInput
                  key={`${step.key}.${formInput.key}`}
                  step={step}
                  isReadonly={isReadonly}
                  formInput={formInput}
                  formValuesConfiguration={valuesConfiguration}
                  formValues={values}
                  onUploadingChange={handleUploadingChange}
                />
              )
            }
          })}

          <DesignBox.FlexGradient
            justify="right"
            direction="up"
            style={{
              position: 'sticky',
              bottom: 0,
              pointerEvents: 'none',
            }}
          >
            <Flex
              className="pb-4 w-full"
              justify="space-between"
              style={{ pointerEvents: 'initial' }}
            >
              {onBack ? (
                <Button
                  icon={<ArrowLeftOutlined />}
                  type="text"
                  onClick={onBack}
                >
                  Back
                </Button>
              ) : (
                <span />
              )}

              {!isReadonly && (
                <Button
                  disabled={isSubmitDisabled}
                  type="primary"
                  htmlType="submit"
                  size="large"
                >
                  {isLastStep ? <>Submit 🎉</> : 'Next'}
                </Button>
              )}
            </Flex>
          </DesignBox.FlexGradient>
        </Form>
      </Flex>
    </>
  )
}
